import React, { useState, useEffect } from 'react';
import { useShop } from '../../context/ShopContext';
import { formatINR } from '../../data/products';
import { 
  Calculator, 
  Sparkles, 
  Scale, 
  Percent, 
  ShieldCheck, 
  FileText, 
  Printer, 
  Lock, 
  Unlock, 
  Copy, 
  Check, 
  RefreshCw, 
  TrendingUp, 
  Share2, 
  ArrowRight,
  Info,
  DollarSign,
  Gem
} from 'lucide-react';

export default function AdminPriceCalculator() {
  const { masterMetalRates, liveGoldRates, addToast } = useShop();

  // Selected Metal & Purity
  const [metalType, setMetalType] = useState('gold'); // 'gold', 'silver', 'platinum'
  const [purityKey, setPurityKey] = useState('22KT (916)');
  const [ratePerGram, setRatePerGram] = useState(6765);

  // Weights
  const [grossWeight, setGrossWeight] = useState(15.5);
  const [stoneWeight, setStoneWeight] = useState(0.5); // grams of stone/enamel
  const [netWeight, setNetWeight] = useState(15.0);

  // Making charges & Wastage
  const [makingChargeType, setMakingChargeType] = useState('percent'); // 'percent' or 'fixed'
  const [makingChargeValue, setMakingChargeValue] = useState(10); // 10% or ₹ per gram
  const [wastagePercent, setWastagePercent] = useState(0); // 0% by default in transparent billing

  // Stones & Diamonds
  const [diamondCarat, setDiamondCarat] = useState(0);
  const [diamondRatePerCarat, setDiamondRatePerCarat] = useState(65000);
  const [gemstoneCharges, setGemstoneCharges] = useState(0);

  // Certification & Hallmark
  const [includeHallmark, setIncludeHallmark] = useState(true);
  const [hallmarkFee, setHallmarkFee] = useState(45); // BIS standard fee
  const [certificateFee, setCertificateFee] = useState(0);

  // Discounts
  const [discountType, setDiscountType] = useState('making'); // 'making' (discount on making %) or 'flat'
  const [discountValue, setDiscountValue] = useState(0);

  // GST
  const [gstRate, setGstRate] = useState(3); // Statutory 3% on Gold/Jewellery

  // Price Lock Status
  const [priceLocked, setPriceLocked] = useState(false);
  const [lockedTimestamp, setLockedTimestamp] = useState(null);
  const [copiedQuote, setCopiedQuote] = useState(false);

  // Sync rate when metal or purity changes
  useEffect(() => {
    if (metalType === 'gold') {
      const gList = masterMetalRates?.gold || [];
      const match = gList.find(g => g.label.includes(purityKey) || purityKey.includes(g.label));
      if (match) {
        setRatePerGram(Number(match.rate) || 6765);
      } else if (purityKey.includes('24')) {
        setRatePerGram(Number(liveGoldRates?.gold24K) || 7380);
      } else if (purityKey.includes('22')) {
        setRatePerGram(Number(liveGoldRates?.gold22K) || 6765);
      } else if (purityKey.includes('18')) {
        setRatePerGram(Number(liveGoldRates?.gold18K) || 5535);
      } else if (purityKey.includes('14')) {
        setRatePerGram(4310);
      } else {
        setRatePerGram(6765);
      }
    } else if (metalType === 'silver') {
      const sList = masterMetalRates?.silver || [];
      const match = sList.find(s => s.label.includes(purityKey) || purityKey.includes(s.label));
      if (match) {
        setRatePerGram(Number(match.rate) || 89.5);
      } else if (purityKey.includes('925')) {
        setRatePerGram(82.5);
      } else {
        setRatePerGram(Number(liveGoldRates?.silver999) || 89.5);
      }
    } else if (metalType === 'platinum') {
      setRatePerGram(3450);
    }
  }, [metalType, purityKey, masterMetalRates, liveGoldRates]);

  // Keep net weight in sync with gross & stone weight
  useEffect(() => {
    const net = Math.max(0, Number(grossWeight || 0) - Number(stoneWeight || 0));
    setNetWeight(parseFloat(net.toFixed(3)));
  }, [grossWeight, stoneWeight]);

  // Calculation Math
  const metalValue = netWeight * ratePerGram;
  const wastageWeight = (netWeight * Number(wastagePercent || 0)) / 100;
  const wastageValue = wastageWeight * ratePerGram;

  // Making charges calculation
  let rawMakingCharge = 0;
  if (makingChargeType === 'percent') {
    rawMakingCharge = (metalValue * Number(makingChargeValue || 0)) / 100;
  } else {
    // Fixed amount per gram
    rawMakingCharge = netWeight * Number(makingChargeValue || 0);
  }

  // Making charge discount
  let makingDiscount = 0;
  let flatDiscount = 0;
  if (discountType === 'making') {
    makingDiscount = (rawMakingCharge * Number(discountValue || 0)) / 100;
  } else {
    flatDiscount = Number(discountValue || 0);
  }
  const netMakingCharge = Math.max(0, rawMakingCharge - makingDiscount);

  // Stone & Diamond values
  const diamondValue = Number(diamondCarat || 0) * Number(diamondRatePerCarat || 0);
  const totalStonesValue = diamondValue + Number(gemstoneCharges || 0);

  // Statutory Fees
  const fees = (includeHallmark ? Number(hallmarkFee || 0) : 0) + Number(certificateFee || 0);

  // Taxable Subtotal
  const preTaxSubtotal = Math.max(0, metalValue + wastageValue + netMakingCharge + totalStonesValue + fees - flatDiscount);

  // GST Calculation
  const gstAmount = (preTaxSubtotal * Number(gstRate || 0)) / 100;

  // Final Total
  const finalPrice = Math.round(preTaxSubtotal + gstAmount);

  // Handlers
  const handleToggleLock = () => {
    if (priceLocked) {
      setPriceLocked(false);
      setLockedTimestamp(null);
      addToast?.('Price unlocked to live rate tracking', 'info');
    } else {
      setPriceLocked(true);
      setLockedTimestamp(new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }));
      addToast?.(`Price locked at ₹${formatINR(finalPrice)} (Valid for 2 Hours)`, 'success');
    }
  };

  const handleCopyQuote = () => {
    const summary = `*M.L. JEWELLERS & KITCHENWARE — OFFICIAL ESTIMATE*\n` +
      `-----------------------------------------\n` +
      `Metal: ${purityKey} ${metalType.toUpperCase()}\n` +
      `Gross Wt: ${grossWeight}g | Net Wt: ${netWeight}g\n` +
      `Live Rate: ₹${ratePerGram}/g\n` +
      `Metal Value: ${formatINR(Math.round(metalValue))}\n` +
      `Making Charges: ${formatINR(Math.round(netMakingCharge))}\n` +
      (diamondValue > 0 ? `Diamonds (${diamondCarat} Ct): ${formatINR(Math.round(diamondValue))}\n` : '') +
      (includeHallmark ? `BIS Hallmark (HUID): ₹${hallmarkFee}\n` : '') +
      `Statutory GST (${gstRate}%): ${formatINR(Math.round(gstAmount))}\n` +
      `-----------------------------------------\n` +
      `*ESTIMATED FINAL PAYABLE: ${formatINR(finalPrice)}*\n` +
      `Quote Generated: ${new Date().toLocaleDateString('en-IN')} ${new Date().toLocaleTimeString('en-IN')}\n` +
      `Showroom: M.I. Road, Jaipur | Contact: +91 98100 23456`;

    navigator.clipboard.writeText(summary);
    setCopiedQuote(true);
    setTimeout(() => setCopiedQuote(false), 2500);
    addToast?.('Quotation copied to clipboard for WhatsApp sharing', 'success');
  };

  return (
    <div style={{ maxWidth: '1240px', margin: '0 auto' }}>
      
      {/* Header Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
        borderRadius: '8px',
        padding: '1.4rem 1.8rem',
        color: '#ffffff',
        marginBottom: '1.5rem',
        boxShadow: '0 4px 15px rgba(30, 60, 114, 0.2)',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem'
      }}>
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: 'rgba(255,255,255,0.2)', padding: '0.2rem 0.6rem', borderRadius: '4px', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.5px', marginBottom: '0.4rem' }}>
            <Calculator size={13} />
            <span>TRANSPARENT BULLION VALUATION ENGINE</span>
          </div>
          <h2 style={{ fontSize: '1.55rem', fontWeight: 800, margin: 0 }}>
            Jewellery Price & Quotation Calculator
          </h2>
          <p style={{ margin: '0.3rem 0 0', opacity: 0.88, fontSize: '0.84rem' }}>
            Instant price calculation, making charge discount rules, stone valuation & statutory 3% GST calculation.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '0.7rem', flexWrap: 'wrap' }}>
          <button
            onClick={handleToggleLock}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.45rem',
              padding: '0.6rem 1.1rem',
              borderRadius: '6px',
              border: 'none',
              background: priceLocked ? '#ff9800' : 'rgba(255, 255, 255, 0.2)',
              color: '#ffffff',
              fontWeight: 700,
              fontSize: '0.84rem',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            {priceLocked ? <Lock size={15} /> : <Unlock size={15} />}
            <span>{priceLocked ? `Price Locked (${lockedTimestamp})` : 'Lock Rate (2 Hrs)'}</span>
          </button>

          <button
            onClick={handleCopyQuote}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.45rem',
              padding: '0.6rem 1.1rem',
              borderRadius: '6px',
              border: 'none',
              background: '#ffe500',
              color: '#1a365d',
              fontWeight: 800,
              fontSize: '0.84rem',
              cursor: 'pointer',
              boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
            }}
          >
            {copiedQuote ? <Check size={16} /> : <Copy size={16} />}
            <span>{copiedQuote ? 'Copied to Clipboard!' : 'Share WhatsApp Quote'}</span>
          </button>
        </div>
      </div>

      {/* Main Grid: Inputs vs Real-time Breakdown */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 1.4fr) minmax(320px, 1fr)', gap: '1.5rem', alignItems: 'start' }}>
        
        {/* Left Column: Configuration Controls */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          
          {/* Card 1: Metal & Purity Selector */}
          <div style={{ background: '#ffffff', borderRadius: '8px', padding: '1.3rem', border: '1px solid #e0e0e0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', borderBottom: '1px solid #f0f0f0', paddingBottom: '0.6rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 800, color: '#212121', fontSize: '0.95rem' }}>
                <Sparkles size={17} color="#f59e0b" />
                <span>1. Metal Type & Carat Purity</span>
              </div>
              <span style={{ fontSize: '0.75rem', color: '#10b981', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                <TrendingUp size={13} /> Live Market Linked
              </span>
            </div>

            {/* Metal Tabs */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem', marginBottom: '1rem' }}>
              {[
                { id: 'gold', label: 'Yellow / Rose Gold', badge: 'Popular' },
                { id: 'silver', label: 'Silver (999/925)', badge: 'Bullion' },
                { id: 'platinum', label: 'Platinum 950', badge: 'Prestige' }
              ].map(m => (
                <button
                  key={m.id}
                  onClick={() => {
                    setMetalType(m.id);
                    if (m.id === 'gold') setPurityKey('22KT (916)');
                    if (m.id === 'silver') setPurityKey('999 Pure Silver');
                    if (m.id === 'platinum') setPurityKey('Platinum 950');
                  }}
                  style={{
                    padding: '0.65rem 0.5rem',
                    borderRadius: '6px',
                    border: metalType === m.id ? '2px solid #2874f0' : '1px solid #e0e0e0',
                    background: metalType === m.id ? '#eef4ff' : '#fafafa',
                    color: metalType === m.id ? '#2874f0' : '#424242',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.15s'
                  }}
                >
                  <div>{m.label}</div>
                  <span style={{ fontSize: '0.65rem', color: '#888', fontWeight: 500 }}>{m.badge}</span>
                </button>
              ))}
            </div>

            {/* Purity & Rate per Gram */}
            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#616161', marginBottom: '0.4rem' }}>
                  Select Hallmark Purity:
                </label>
                <select
                  value={purityKey}
                  onChange={(e) => setPurityKey(e.target.value)}
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '4px', border: '1px solid #d0d7de', fontSize: '0.88rem', fontWeight: 600, background: '#fff' }}
                >
                  {metalType === 'gold' && (
                    <>
                      <option value="24KT (999)">24KT (999) — Pure Gold Bar / Coin</option>
                      <option value="22KT (916)">22KT (916) — Traditional Jewellery</option>
                      <option value="18KT (750)">18KT (750) — Diamond Jewellery Standard</option>
                      <option value="14KT (585)">14KT (585) — Lightweight & Daily Wear</option>
                      <option value="9KT (375)">9KT (375) — Contemporary Studs</option>
                    </>
                  )}
                  {metalType === 'silver' && (
                    <>
                      <option value="999 Pure Silver">999 Fine Silver (Coins & Utensils)</option>
                      <option value="925 Sterling Silver">925 Sterling Silver (Ornaments)</option>
                    </>
                  )}
                  {metalType === 'platinum' && (
                    <option value="Platinum 950">Pt 950 — 95% Pure Platinum</option>
                  )}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#616161', marginBottom: '0.4rem' }}>
                  Rate Per Gram (₹):
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="number"
                    value={ratePerGram}
                    disabled={priceLocked}
                    onChange={(e) => setRatePerGram(Number(e.target.value) || 0)}
                    style={{
                      width: '100%',
                      padding: '0.6rem 0.75rem',
                      borderRadius: '4px',
                      border: '1px solid #d0d7de',
                      fontSize: '0.92rem',
                      fontWeight: 800,
                      color: '#1a365d',
                      background: priceLocked ? '#f5f5f5' : '#ffffff'
                    }}
                  />
                  {priceLocked && (
                    <span style={{ position: 'absolute', right: '10px', top: '10px', fontSize: '0.7rem', color: '#ff9800', fontWeight: 700 }}>
                      LOCKED
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Card 2: Weights & Stone Deductions */}
          <div style={{ background: '#ffffff', borderRadius: '8px', padding: '1.3rem', border: '1px solid #e0e0e0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 800, color: '#212121', fontSize: '0.95rem', marginBottom: '1rem', borderBottom: '1px solid #f0f0f0', paddingBottom: '0.6rem' }}>
              <Scale size={17} color="#2874f0" />
              <span>2. Ornament Weight Specifications</span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.9rem', marginBottom: '0.8rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#616161', marginBottom: '0.4rem' }}>
                  Gross Weight (Grams):
                </label>
                <input
                  type="number"
                  step="0.001"
                  value={grossWeight}
                  onChange={(e) => setGrossWeight(parseFloat(e.target.value) || 0)}
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '4px', border: '1px solid #d0d7de', fontSize: '0.92rem', fontWeight: 700 }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#616161', marginBottom: '0.4rem' }}>
                  Stones / Enamel (g):
                </label>
                <input
                  type="number"
                  step="0.001"
                  value={stoneWeight}
                  onChange={(e) => setStoneWeight(parseFloat(e.target.value) || 0)}
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '4px', border: '1px solid #d0d7de', fontSize: '0.92rem', fontWeight: 700 }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 800, color: '#2e7d32', marginBottom: '0.4rem' }}>
                  Net Metal Weight (g):
                </label>
                <input
                  type="number"
                  readOnly
                  value={netWeight}
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '4px', border: '2px solid #a7f3d0', background: '#ecfdf5', fontSize: '0.95rem', fontWeight: 800, color: '#065f46' }}
                />
              </div>
            </div>

            {/* Quick Weight Presets */}
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: '0.72rem', color: '#757575', fontWeight: 600 }}>Quick Presets:</span>
              {[
                { label: 'Ring (4.5g)', val: 4.5 },
                { label: 'Pendant (8.2g)', val: 8.2 },
                { label: 'Earrings (14g)', val: 14.0 },
                { label: 'Chain (22g)', val: 22.0 },
                { label: 'Bangles (45g)', val: 45.0 },
                { label: 'Bridal Set (85g)', val: 85.0 }
              ].map(p => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => {
                    setGrossWeight(p.val);
                    setStoneWeight(0);
                  }}
                  style={{
                    background: '#f1f3f6',
                    border: '1px solid #e0e0e0',
                    borderRadius: '3px',
                    padding: '0.25rem 0.55rem',
                    fontSize: '0.72rem',
                    color: '#424242',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Card 3: Making Charges & Wastage */}
          <div style={{ background: '#ffffff', borderRadius: '8px', padding: '1.3rem', border: '1px solid #e0e0e0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', borderBottom: '1px solid #f0f0f0', paddingBottom: '0.6rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 800, color: '#212121', fontSize: '0.95rem' }}>
                <Percent size={17} color="#8b5cf6" />
                <span>3. Making Charges & Artisan Fees</span>
              </div>
              <div style={{ display: 'flex', gap: '0.3rem' }}>
                <button
                  type="button"
                  onClick={() => setMakingChargeType('percent')}
                  style={{
                    padding: '0.25rem 0.6rem',
                    fontSize: '0.72rem',
                    borderRadius: '3px',
                    border: 'none',
                    background: makingChargeType === 'percent' ? '#2874f0' : '#f0f0f0',
                    color: makingChargeType === 'percent' ? '#fff' : '#424242',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  Percentage (%)
                </button>
                <button
                  type="button"
                  onClick={() => setMakingChargeType('fixed')}
                  style={{
                    padding: '0.25rem 0.6rem',
                    fontSize: '0.72rem',
                    borderRadius: '3px',
                    border: 'none',
                    background: makingChargeType === 'fixed' ? '#2874f0' : '#f0f0f0',
                    color: makingChargeType === 'fixed' ? '#fff' : '#424242',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  Per Gram (₹)
                </button>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.9rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#616161', marginBottom: '0.4rem' }}>
                  {makingChargeType === 'percent' ? 'Making Charge (%)' : 'Rate / Gram (₹):'}
                </label>
                <input
                  type="number"
                  step="0.5"
                  value={makingChargeValue}
                  onChange={(e) => setMakingChargeValue(Number(e.target.value) || 0)}
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '4px', border: '1px solid #d0d7de', fontSize: '0.92rem', fontWeight: 700 }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#616161', marginBottom: '0.4rem' }}>
                  Artisan Wastage / Ghat (%):
                </label>
                <input
                  type="number"
                  step="0.5"
                  value={wastagePercent}
                  onChange={(e) => setWastagePercent(Number(e.target.value) || 0)}
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '4px', border: '1px solid #d0d7de', fontSize: '0.92rem', fontWeight: 700 }}
                />
                <span style={{ fontSize: '0.68rem', color: '#10b981', fontWeight: 600 }}>0% in ML Jewellers direct billing</span>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#d32f2f', marginBottom: '0.4rem' }}>
                  Discount on Making (%):
                </label>
                <input
                  type="number"
                  step="5"
                  value={discountValue}
                  onChange={(e) => setDiscountValue(Number(e.target.value) || 0)}
                  placeholder="e.g. 50% Off"
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '4px', border: '1px solid #fca5a5', background: '#fff5f5', fontSize: '0.92rem', fontWeight: 800, color: '#b91c1c' }}
                />
              </div>
            </div>
          </div>

          {/* Card 4: Diamonds, Gemstones & Certifications */}
          <div style={{ background: '#ffffff', borderRadius: '8px', padding: '1.3rem', border: '1px solid #e0e0e0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 800, color: '#212121', fontSize: '0.95rem', marginBottom: '1rem', borderBottom: '1px solid #f0f0f0', paddingBottom: '0.6rem' }}>
              <Gem size={17} color="#0284c7" />
              <span>4. Diamonds, Stones & BIS Hallmark</span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.9rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#616161', marginBottom: '0.4rem' }}>
                  Diamond Weight (Carat):
                </label>
                <input
                  type="number"
                  step="0.05"
                  value={diamondCarat}
                  onChange={(e) => setDiamondCarat(parseFloat(e.target.value) || 0)}
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '4px', border: '1px solid #d0d7de', fontSize: '0.92rem', fontWeight: 700 }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#616161', marginBottom: '0.4rem' }}>
                  Diamond Rate / Carat (₹):
                </label>
                <input
                  type="number"
                  step="5000"
                  value={diamondRatePerCarat}
                  onChange={(e) => setDiamondRatePerCarat(Number(e.target.value) || 0)}
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '4px', border: '1px solid #d0d7de', fontSize: '0.92rem', fontWeight: 700 }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#616161', marginBottom: '0.4rem' }}>
                  Precious Gemstones (₹):
                </label>
                <input
                  type="number"
                  value={gemstoneCharges}
                  onChange={(e) => setGemstoneCharges(Number(e.target.value) || 0)}
                  placeholder="Ruby, Emerald etc."
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '4px', border: '1px solid #d0d7de', fontSize: '0.92rem', fontWeight: 700 }}
                />
              </div>
            </div>

            {/* Checkbox for BIS Hallmark & Certificate */}
            <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', background: '#f8fafc', padding: '0.75rem 1rem', borderRadius: '6px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.84rem', fontWeight: 700, color: '#334155' }}>
                <input
                  type="checkbox"
                  checked={includeHallmark}
                  onChange={(e) => setIncludeHallmark(e.target.checked)}
                  style={{ width: '16px', height: '16px', accentColor: '#2874f0' }}
                />
                <span>Include BIS Hallmark Fee (₹{hallmarkFee})</span>
              </label>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '0.82rem', color: '#64748b', fontWeight: 600 }}>Lab Certificate (IGI/GIA): ₹</span>
                <input
                  type="number"
                  value={certificateFee}
                  onChange={(e) => setCertificateFee(Number(e.target.value) || 0)}
                  style={{ width: '80px', padding: '0.35rem 0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '0.82rem', fontWeight: 700 }}
                />
              </div>
            </div>
          </div>

        </div>

        {/* Right Column: Real-time Itemized Quotation Invoice */}
        <div style={{ position: 'sticky', top: '80px' }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '8px',
            border: '2px solid #2874f0',
            boxShadow: '0 8px 30px rgba(40, 116, 240, 0.12)',
            overflow: 'hidden'
          }}>
            {/* Ticket Header */}
            <div style={{ background: '#2874f0', color: '#ffffff', padding: '1rem 1.4rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: '0.75rem', letterSpacing: '0.8px', textTransform: 'uppercase', opacity: 0.9, fontWeight: 700 }}>ESTIMATION BILLING SUMMARY</div>
                <div style={{ fontSize: '1.15rem', fontWeight: 900 }}>M.L. Jewellers Vault Billing</div>
              </div>
              <ShieldCheck size={28} color="#ffe500" />
            </div>

            {/* Spec Pill */}
            <div style={{ padding: '0.8rem 1.4rem', background: '#eef4ff', borderBottom: '1px solid #dce7f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.82rem' }}>
              <div>
                <strong style={{ color: '#1e3c72' }}>{purityKey} {metalType.toUpperCase()}</strong>
                <span style={{ color: '#555', marginLeft: '0.4rem' }}>({netWeight}g net weight)</span>
              </div>
              <span style={{ background: '#2874f0', color: '#fff', padding: '0.15rem 0.5rem', borderRadius: '3px', fontWeight: 700, fontSize: '0.72rem' }}>
                ₹{ratePerGram}/g
              </span>
            </div>

            {/* Price Breakdown Table */}
            <div style={{ padding: '1.2rem 1.4rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <tbody>
                  <tr style={{ borderBottom: '1px dashed #e2e8f0' }}>
                    <td style={{ padding: '0.55rem 0', color: '#64748b' }}>Pure Metal Value ({netWeight}g × ₹{ratePerGram})</td>
                    <td style={{ padding: '0.55rem 0', textAlign: 'right', fontWeight: 700, color: '#1e293b' }}>
                      {formatINR(Math.round(metalValue))}
                    </td>
                  </tr>

                  {wastageValue > 0 && (
                    <tr style={{ borderBottom: '1px dashed #e2e8f0' }}>
                      <td style={{ padding: '0.55rem 0', color: '#64748b' }}>Artisan Wastage ({wastagePercent}%)</td>
                      <td style={{ padding: '0.55rem 0', textAlign: 'right', fontWeight: 700, color: '#1e293b' }}>
                        +{formatINR(Math.round(wastageValue))}
                      </td>
                    </tr>
                  )}

                  <tr style={{ borderBottom: '1px dashed #e2e8f0' }}>
                    <td style={{ padding: '0.55rem 0', color: '#64748b' }}>
                      Making Charges {makingChargeType === 'percent' ? `(${makingChargeValue}%)` : `(₹${makingChargeValue}/g)`}
                      {makingDiscount > 0 && <span style={{ color: '#10b981', marginLeft: '0.3rem' }}>({discountValue}% OFF)</span>}
                    </td>
                    <td style={{ padding: '0.55rem 0', textAlign: 'right', fontWeight: 700, color: '#1e293b' }}>
                      {makingDiscount > 0 ? (
                        <>
                          <span style={{ textDecoration: 'line-through', color: '#94a3b8', fontSize: '0.75rem', marginRight: '0.4rem' }}>
                            {formatINR(Math.round(rawMakingCharge))}
                          </span>
                          <span style={{ color: '#10b981' }}>{formatINR(Math.round(netMakingCharge))}</span>
                        </>
                      ) : (
                        formatINR(Math.round(netMakingCharge))
                      )}
                    </td>
                  </tr>

                  {diamondValue > 0 && (
                    <tr style={{ borderBottom: '1px dashed #e2e8f0' }}>
                      <td style={{ padding: '0.55rem 0', color: '#64748b' }}>Natural Diamonds ({diamondCarat} Ct)</td>
                      <td style={{ padding: '0.55rem 0', textAlign: 'right', fontWeight: 700, color: '#1e293b' }}>
                        +{formatINR(Math.round(diamondValue))}
                      </td>
                    </tr>
                  )}

                  {gemstoneCharges > 0 && (
                    <tr style={{ borderBottom: '1px dashed #e2e8f0' }}>
                      <td style={{ padding: '0.55rem 0', color: '#64748b' }}>Colored Gemstones / Enamel</td>
                      <td style={{ padding: '0.55rem 0', textAlign: 'right', fontWeight: 700, color: '#1e293b' }}>
                        +{formatINR(Math.round(gemstoneCharges))}
                      </td>
                    </tr>
                  )}

                  {fees > 0 && (
                    <tr style={{ borderBottom: '1px dashed #e2e8f0' }}>
                      <td style={{ padding: '0.55rem 0', color: '#64748b' }}>Hallmark (HUID) & Lab Cert.</td>
                      <td style={{ padding: '0.55rem 0', textAlign: 'right', fontWeight: 700, color: '#1e293b' }}>
                        +{formatINR(fees)}
                      </td>
                    </tr>
                  )}

                  <tr style={{ borderBottom: '1px solid #cbd5e1', background: '#f8fafc' }}>
                    <td style={{ padding: '0.65rem 0.5rem', fontWeight: 700, color: '#475569' }}>Taxable Net Subtotal</td>
                    <td style={{ padding: '0.65rem 0.5rem', textAlign: 'right', fontWeight: 800, color: '#0f172a' }}>
                      {formatINR(Math.round(preTaxSubtotal))}
                    </td>
                  </tr>

                  <tr style={{ borderBottom: '2px solid #2874f0' }}>
                    <td style={{ padding: '0.6rem 0', color: '#64748b', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                      <span>Statutory GST ({gstRate}%)</span>
                      <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>(CGST 1.5% + SGST 1.5%)</span>
                    </td>
                    <td style={{ padding: '0.6rem 0', textAlign: 'right', fontWeight: 700, color: '#1e293b' }}>
                      +{formatINR(Math.round(gstAmount))}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Grand Total */}
              <div style={{ marginTop: '1.2rem', padding: '1rem', background: '#f0fdf4', borderRadius: '6px', border: '1px solid #86efac', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: '0.72rem', fontWeight: 800, color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    FINAL ESTIMATED PAYABLE
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#65a30d' }}>
                    All inclusive of Taxes & Hallmark
                  </div>
                </div>
                <div style={{ fontSize: '1.75rem', fontWeight: 900, color: '#14532d', fontFamily: 'var(--font-serif)' }}>
                  {formatINR(finalPrice)}
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '0.6rem', marginTop: '1.2rem' }}>
                <button
                  type="button"
                  onClick={handleCopyQuote}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.4rem',
                    padding: '0.7rem',
                    borderRadius: '4px',
                    border: 'none',
                    background: '#2874f0',
                    color: '#ffffff',
                    fontWeight: 700,
                    fontSize: '0.84rem',
                    cursor: 'pointer'
                  }}
                >
                  <Share2 size={15} />
                  <span>Send Quote</span>
                </button>

                <button
                  type="button"
                  onClick={() => window.print()}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.4rem',
                    padding: '0.7rem 1rem',
                    borderRadius: '4px',
                    border: '1px solid #cbd5e1',
                    background: '#ffffff',
                    color: '#334155',
                    fontWeight: 700,
                    fontSize: '0.84rem',
                    cursor: 'pointer'
                  }}
                >
                  <Printer size={15} />
                  <span>Print Slip</span>
                </button>
              </div>

              <div style={{ marginTop: '0.9rem', fontSize: '0.72rem', color: '#94a3b8', textAlign: 'center' }}>
                Calculated in compliance with Bureau of Indian Standards (BIS) hallmark and statutory GST regulations.
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
